import React, { useEffect, useState, useMemo, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Label, Tooltip } from 'recharts';
import { makeStyles, createStyles } from '@material-ui/core/styles';

import {
  MeasureInfoType,
  SelectDataType,
  GetJudgeSetting,
  GetChartData,
  GraphLineDataType,
  JudgeSettingType,
  GetComparisonNo,
} from './FFTComparison';
import dayjs from 'dayjs';
import * as Constant from '../../../Constant';
import { ToValueMaxRound, ToValueMinRound } from '../../../function/CalculationModel';
import { useTranslation } from 'react-i18next';
import { CcTypKind } from '../../../Constant';

const useStyles = makeStyles(
  createStyles({
    customTooltip: {
      background: '#FFFFEE',
      fontSize: '14px',
      padding: '10px 10px 0px 20px',
    },
  })
);

export type GraphDispSetting = {
  yMin: number;
  yMax: number;
  xMin: number;
  xMax: number;
  selectAxis: string;
  isPileup: boolean;

  isThreshold1: boolean;
  isThreshold2: boolean;
  isThreshold3: boolean;

  isShow1: boolean;
  isShow2: boolean;
  isShow3: boolean;

  unitSelectIndex: number;
};

export type GraphData = {
  lineNo: number;
  lineName: string;
  points: GraphPoint[];
  valueUpper: number;
  valueUpperLimit: number;
  selectFlg: boolean;
  infoColor: string;
  yValueMax: number; // 表示対象の範囲内での最大値
  yValueMin: number; // 表示対象の範囲内での最大値
  tooltipColor: string;
  /**
   * OA値
   */
  oaValue: string;
};
export type GraphPoint = {
  xValue: number;
  yValue: number;
};

export type ChartUpDateInfo = {
  line1: SelectDataType;
  line2: SelectDataType;
  line3: SelectDataType;
  mesList: MeasureInfoType[];
  isInitDisp: boolean; //初期表示フラグ（画面遷移から来た時の表示時にtrue,ボタン表示時false)
};

type GraphSetting = {
  yMin: number;
  yMax: number;
  xMin: number;
  xMax: number;
  backColor: string;
  memoryFontColor: string;
  yUnit: string;
  xUnit: string;
  yUnitColor: string;
  xUnitColor: string;
  gridColor: string;
};

type FetchDataType = {
  lineDatas: GraphLineDataType[];
  chartDataInfo: ChartUpDateInfo;
};

type Props = {
  chartInfo: ChartUpDateInfo;
  graphDispSetting: GraphDispSetting;
  onChangeGraphDataEvent: (GraphDatas: GraphData[], yMin: number, yMax: number, yUnit: string) => void;
  chartContainerStyle: string;
  updateLoadginState: (value: boolean) => void;
  isFFTAutoRangeRef: boolean;
  onFFTAutoRamgeSet: (data: boolean) => void;
};

/**
 * 重、軽故障ライン　DB差分時値変換処理
 * @param valueMax
 * @param value
 * @param isdb
 * @returns
 */
const getLIMValue = (value: number, isdb: boolean): number => {
  if (isdb) {
    return value === 0 ? 0 : value * -1;
  } else {
    return value;
  }
};



/**
 *グラフのポイントリストを作成して返します
 * @param data
 * @param minValue
 * @param maxValue
 * @param size
 * @param samplingRate
 * @param isdb
 * @returns
 */

const createChartPoints = (
  data: GraphLineDataType,
  minValue: number,
  maxValue: number,
  size: number,
  samplingRate: number,
  isdb: boolean
): Array<GraphPoint> => {
  //console.debug(['==createChartPoints==', data]);
  var points = new Array<GraphPoint>();
  var skip = Math.ceil(minValue * size * samplingRate * 2);
  var count = Math.ceil((maxValue - minValue) * size * samplingRate * 2) + 1;
  var yValueMax = Math.max(...data.YValues?.map((p) => p));

  var index = 0;
  for (let i = 0; i < data.XValues.length; i++) {
    if (skip <= i) {
      if (index <= count) {
        var x = Number(data.XValues[i]);
        var y = data.YValues[i];
        if (isdb) {
          y = y === 0 ? 0 : Math.log10(Math.abs(y) / yValueMax) * 20;
        }
        points.push({
          xValue: x,
          yValue: y,
        });
        index++;
      }
    }
  }
  return points;
};

/**
 * グラフの線データを更新します
 * @param data グラフラインデータ
 * @param lineColor
 * @param xmin
 * @param xmax
 * @param isdb
 * @returns
 */
const upDateGraphData = (
  lineNo: number,
  data: GraphLineDataType,
  lineColor: string,
  xmin: number,
  xmax: number,
  isdb: boolean,
  isShow: boolean
): Promise<GraphData | undefined> => {
  return new Promise((resolve, reject) => {
    if (data !== undefined) {
      var item: GraphData = {
        lineNo: lineNo,
        valueUpper: 0,
        valueUpperLimit: 0,
        selectFlg: isShow,
        points: [],
        infoColor: lineColor,
        tooltipColor: lineColor,
        lineName: data.ItemName,
        yValueMax: 0,
        yValueMin: 0,
        oaValue: '',
      };

      var points = createChartPoints(data, xmin, xmax, data.SignalSize, data.SampleSpan, isdb);
      item.points = points;
      item.oaValue = data.OAValue;
      console.debug(item);
      resolve(item);
    }
    resolve(undefined);
  });
};

/**
 *
 * @param mesList
 * @param selectDt
 * @returns
 */
const getTargetData = (
  mesList: MeasureInfoType[],
  selectDt: SelectDataType
): Promise<GraphLineDataType | undefined> => {
  return new Promise((resolve, reject) => {
    var mes = mesList.find((item) => item.id === selectDt.mesid);
    var dev = mesList.find((item) => item.id === selectDt.devid);
    var itm = mesList.find((item) => item.id === selectDt.itemid);
    var day = dayjs(selectDt.selectDate).format('YYYY-MM-DD');
    var time = selectDt.selectTime;
    var enve = selectDt.envelopeNo;

    if (time === undefined) resolve(undefined);

    var target = mesList.find(
      (item) => item.KindNo === mes?.KindNo && item.DevIndex === dev?.DevIndex && item.ItemNameNo === itm?.ItemNameNo //&& item.EnveNameNo === itm?.ItemNameNo
    );

    var comparisonNo = GetComparisonNo(target);

    if (target !== undefined) {
      //console.debug(["★test target1 ", target]);
      if (target.PlcNo == 0) {
        resolve(undefined);
      }
      GetChartData(target.PlcNo, target.AiCard, target.AiCh, `${day} ${time}`, comparisonNo, enve).then((ret) => {
        if (ret !== undefined) {
          //console.debug(['GetTargetDataEx End', ret]);
          ret.lineNo = selectDt.lineNo;
          resolve(ret);
        } else {
          reject('Invalidate response');
        }
      });
    } else {
      reject('Invalidate response');
    }
  });
};

/**
 * 計測種別で単位を更新します。
 * @param kindNo 計測種別
 * @param unit 単位
 * @returns
 */
const updateUnitByMesKind = (/* speed: number,  */ kindNo: number, unit: string): string => {
  //if (speed == SpeedKind.High)
  switch (kindNo) {
    case Constant.MeasureHighKind.VIBRATION_SPD:
      // 振動（速度）
      return 'mm/s';
    case Constant.MeasureHighKind.VIBRATION_DSP:
      // 振動（変位）
      return 'µm';
  }
  //}
  return unit;
};

/**
 * FFT比較画面のチャートobject
 * @param props
 * @returns
 */
const FFTComparisonChart: React.FC<Props> = (props) => {
  useEffect(() => {
    console.debug(['FFTComparisonChart ', props]);
  });

  const { t } = useTranslation();

  const classes = useStyles();
  const { onChangeGraphDataEvent } = props;
  // 1本分のグラフデータの配列
  const [graphData, setGraphData] = useState<GraphData[]>([]);
  // 閾値グラフデータ
  const [thresholdgraphData, setThresholdgraphData] = useState<GraphData[]>([]);
  // 閾値グラフラインをホールド
  const thresholdgraphLine = useRef<string | null>(null);
  // 閾値グラフのカラー
  const thresholdgrapColor = useRef<string | null>('black');
  // 閾値グラフのy軸の値
  const thresholdgraphYPoint = useRef<string | null>(null);
  //　グラフデータを作成するための情報
  const [fetchData, setFetchData] = useState<FetchDataType>({
    lineDatas: [],
    chartDataInfo: props.chartInfo,
  });

  // グラフ表示スタイル設定
  const [graphSetting, SetGraphSetting] = useState<GraphSetting>({
    yMin: 0,
    yMax: 1,
    xMin: 0,
    xMax: 100,
    backColor: Constant.GraphThemeLight.BACK_COLOR,
    memoryFontColor: Constant.GraphThemeLight.MEMORY_FONT_COLOR,
    yUnit: 'A',
    xUnit: 'Hz',
    yUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    xUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    gridColor: Constant.GraphThemeLight.GRID_COLOR,
  });

  //初期表示判定フラグ
  const isInitDisp = useRef<boolean>(false);

  /**
 *閾値のグラフデータ作成
 * @param data ラインデータ
 * @param isdb　振幅軸の表示モード
 */
  const createGraphThreshold = (
    lineIndex: number,
    lineHide: boolean,
    data: GraphLineDataType,
    isdb: boolean
  ): GraphData[] | undefined => {
    var ret: GraphData[] = [];
    if (data == undefined || data.JudgeSettingModel == undefined) {
      return undefined;
    }

    console.debug(['createGraphThreshold ', data.JudgeSettingModel])

    //診断タイプがdbかそれ以外か
    let selectGraphItems: JudgeSettingType[] = [];
    if (isdb) {
      selectGraphItems = data.JudgeSettingModel.filter((r) => r.ClcTypeNo === CcTypKind.DBD);
    } else {
      selectGraphItems = data.JudgeSettingModel.filter((r) => r.ClcTypeNo !== CcTypKind.DBD);
    }


    for (let i = 0; i < selectGraphItems.length; i++) {
      var graphPoints: GraphPoint[] = [];
      var jset = selectGraphItems[i];
      let freqValue: number = -1;

      if (data.INV_DRV_ON === 0) {
        //インバータ駆動フラグOFFの場合
        freqValue = jset.FreqCenter;
        console.debug(["INV_DRV_ON:OFF", freqValue]);
      }
      else {
        //インバータ駆動フラグONの場合
        let temp = data.TrddatChrModels.filter((r) => r.MCLC_TYP_ID == selectGraphItems[i].MclcTypeID);
        if (temp.length != 0) {
          freqValue = temp[0].FREQ !== undefined ? temp[0].FREQ : -1;
          console.debug(["INV_DRV_ON:ON", freqValue]);
        }

      }

      if (jset.BandValue > 0) {
        if (freqValue !== -1) {
          let x = freqValue - jset.BandValue;
          let y = getLIMValue(Number(jset.SeriousFailure), isdb);
          graphPoints.push({ xValue: x, yValue: y });

          x = freqValue + jset.BandValue;
          y = getLIMValue(Number(jset.SeriousFailure), isdb);

          graphPoints.push({ xValue: x, yValue: y });
        }
        // console.debug([
        //   'createGraphThreshold - valueMax, SeriousFailure(value), isdb,ans',
        //   'value === 0 ? 0 : Math.log10(Math.abs(value) / valueMax) * 20',
        //   data.ValueMax,
        //   Number(jset.SeriousFailure),
        //   isdb,
        //   y,
        // ]);
      } else {
        if (freqValue !== -1) {
          x = freqValue;
          y = getLIMValue(Number(jset.SeriousFailure), isdb);
          graphPoints.push({ xValue: x, yValue: y });
        }
      }

      var almLine: GraphData = {
        lineNo: 10 + lineIndex + i,
        lineName: t('FFTComparisonChart.計測データ', '計測データ') + `${lineIndex}_${jset.AbnormalKind}_` + t('FFTComparisonChart.重故障', '重故障'),
        points: graphPoints,
        valueUpper: 0,
        valueUpperLimit: 0,
        selectFlg: lineHide,
        infoColor: '#f8bbd0', //ffccbc
        tooltipColor: '#f76397',
        yValueMax: 0,
        yValueMin: 0,
        oaValue: '',
      };

      ret.push(almLine);

      graphPoints = [];
      if (jset.BandValue > 0) {
        if (freqValue !== -1) {
          var x = freqValue - jset.BandValue;
          var y = getLIMValue(Number(jset.MinorFailure), isdb);
          graphPoints.push({ xValue: x, yValue: y });
          x = freqValue + jset.BandValue;
          y = getLIMValue(Number(jset.MinorFailure), isdb);
          graphPoints.push({ xValue: x, yValue: y });
        }
        // console.debug([
        //   'createGraphThreshold - valueMax, MinorFailure(value), isdb,ans',
        //   'value === 0 ? 0 : Math.log10(Math.abs(value) / valueMax) * 20',
        //   data.ValueMax,
        //   Number(jset.MinorFailure),
        //   isdb,
        //   y,
        // ]);
      } else {
        if (freqValue !== -1) {
          x = freqValue;
          y = getLIMValue(Number(jset.MinorFailure), isdb);
          graphPoints.push({ xValue: x, yValue: y });
        }
      }

      var wrmLine: GraphData = {
        lineNo: 20 + lineIndex + i,
        lineName: t('FFTComparisonChart.計測データ', '計測データ') + `${lineIndex}_${jset.AbnormalKind}_` + t('FFTComparisonChart.軽故障', '軽故障'),
        points: graphPoints,
        valueUpper: 0,
        valueUpperLimit: 0,
        selectFlg: lineHide,
        infoColor: '#ffccbc',
        tooltipColor: '#ff8080',
        yValueMax: 0,
        yValueMin: 0,
        oaValue: '',
      };

      //console.debug(['createGraphThreshold - wrmLine', wrmLine]);
      ret.push(wrmLine);
    }

    return ret;
  };

  //閾値のラインにマウスが乗った際の処理
  const handleMouseEnter = (dataKey: string, color: string, yPoint: string) => {
    thresholdgraphLine.current = dataKey;
    thresholdgrapColor.current = color;
    thresholdgraphYPoint.current = yPoint;
  };
  //閾値のラインからマウスが離れた際の処理
  const handleMouseLeave = () => {
    thresholdgraphLine.current = null;
  };

  //表示ボタン押下時
  useEffect(() => {
    console.debug(['FFTComparisonChart Update', props.chartInfo]);
    const chinf = props.chartInfo;

    if (chinf.line1.devid == -1 && chinf.line2.devid == -1 && chinf.line3.devid == -1) {
      props.updateLoadginState(false);
      console.debug('表示データ無し');
      return;
    }

    //props.updateLoadginState(true);
    var a = getTargetData(chinf.mesList, chinf.line1);
    var b = getTargetData(chinf.mesList, chinf.line2);
    var c = getTargetData(chinf.mesList, chinf.line3);
    console.debug(['updateLoadginState a b c ', a, b, c]);
    Promise.all([a, b, c])
      .then((ret) => {
        console.debug(['updateLoadginState ret', ret]);
        var dt: GraphLineDataType[] = [];
        if (ret[0]) dt.push(ret[0]);
        if (ret[1]) dt.push(ret[1]);
        if (ret[2]) dt.push(ret[2]);

        console.debug(['setFetchData  , lineDatas, chartDataInfo', dt, chinf]);
        setFetchData({ lineDatas: dt, chartDataInfo: chinf });
      })
      .finally(() => {
        console.debug(['FFTComparisonChart Update finally']);
        //props.updateLoadginState(false);
      });
  }, [props.chartInfo]);

  //グラフ描画処理
  useEffect(() => {
    props.updateLoadginState(true);
    console.debug(['グラフ描画処理開始', fetchData, props.graphDispSetting]);
    const chinf = fetchData.chartDataInfo;
    if (chinf.line1.devid == -1 && chinf.line2.devid == -1 && chinf.line3.devid == -1) {
      return;
    }
    const gSetting = props.graphDispSetting;
    const isdb = gSetting.selectAxis === 'r' ? false : true;

    const promises: Promise<GraphData | undefined>[] = [];
    fetchData.lineDatas.forEach((r) => {
      if (r) {
        let color = '';
        let show: boolean = false;
        switch (r.lineNo) {
          case 1:
            color = chinf.line1.lineColor;
            show = gSetting.isShow1;
            break;
          case 2:
            color = chinf.line2.lineColor;
            show = gSetting.isShow2;
            break;
          case 3:
            color = chinf.line3.lineColor;
            show = gSetting.isShow3;
            break;
        }
        const tmp = upDateGraphData(r.lineNo, r, color, gSetting.xMin, gSetting.xMax, isdb, show);
        promises.push(tmp);
      }
    });

    console.debug(['グラフデータ取得準備 promises', promises]);
    Promise.all(promises)
      .then((ret) => {
        const dt: GraphData[] = [];
        ret.forEach((r) => {
          if (r) dt.push(r);
        });

        console.debug(['グラフデータ取得', ret, dt]);
        var dt2: GraphData[] = [];
        fetchData.lineDatas.forEach((r) => {
          if (r) {
            let isThreshold: boolean = false;
            switch (r.lineNo) {
              case 1:
                isThreshold = props.graphDispSetting.isThreshold1;
                break;
              case 2:
                isThreshold = props.graphDispSetting.isThreshold2;
                break;
              case 3:
                isThreshold = props.graphDispSetting.isThreshold3;
                break;
            }
            //閾値グラフデータ取得
            let tmp = createGraphThreshold(r.lineNo, isThreshold, r, isdb);
            if (tmp) {
              dt2 = dt2.concat(tmp);
            }
            console.debug(['-ThresholdgraphData-tmp', tmp]);
          }
        });
        console.debug(['-GraphData-ThresholdgraphData', dt, dt2]);
        setGraphData(dt);
        setThresholdgraphData(dt2);

        var ans = setYMinMaxEx(dt, gSetting, props.isFFTAutoRangeRef);
        props.onFFTAutoRamgeSet(false);

        const unit = () => {
          if (props.graphDispSetting.selectAxis == 'r') {
            //リニア

            if (!fetchData.lineDatas.length) return '';

            let unitIndex = props.graphDispSetting.unitSelectIndex;

            let devId: number = 0;
            console.debug(['グラフデータ取得 単位', fetchData]);
            let unit: string = fetchData.lineDatas.find((r) => r.lineNo === unitIndex)?.Unit ?? '';
            switch (unitIndex) {
              case 1:
                devId = fetchData.chartDataInfo.line1.devid;
                break;
              case 2:
                devId = fetchData.chartDataInfo.line2.devid;
                break;
              case 3:
                devId = fetchData.chartDataInfo.line3.devid;
                break;
            }
            console.debug(['グラフデータ取得 単位 確定', unit]);
            if (devId != null) {
              const mes = fetchData.chartDataInfo.mesList.find((v) => v.id === devId);
              const kindNo = mes ? mes.KindNo : 0;
              return updateUnitByMesKind(kindNo, unit);
            }
            return unit;
          } else if (props.graphDispSetting.selectAxis == 'd') {
            //dB
            return 'dB';
          }
          return '';
        };
        //console.debug(['fetchData ,GraphData,ThresholdgraphData', fetchData, dt, dt2]);

        const yunit = unit();

        onChangeGraphDataEvent(dt, ans[0], ans[1], yunit);

        SetGraphSetting({
          ...graphSetting,
          yMin: ans[0],
          yMax: ans[1],
          xMin: gSetting.xMin,
          xMax: gSetting.xMax,
          yUnit: yunit,
        });
      })
      .finally(() => {
        console.debug(['グラフ描画処理終了 finally']);
        props.updateLoadginState(false);
      });
  }, [fetchData, props.graphDispSetting]);

  /**
   *Y軸最大、最小値計算
   * @param data
   * @param dispSetting
   */
  const setYMinMax = (data: GraphLineDataType[], dispSetting: GraphDispSetting): [number, number] => {
    var max = 0;
    var min = 0;
    const a = data[0]?.ValueMax;
    const b = data[1]?.ValueMax;
    const c = data[2]?.ValueMax;
    const array1 = [a, b, c];
    max = Math.max(...array1);

    if (dispSetting.selectAxis !== 'r') {
      max = dispSetting.yMax;
      min = dispSetting.yMin;
    }
    //max = ToValueMaxRound(max);
    //min = ToValueMinRound(min);
    return [min, max];
  };

  /**
   *Y軸最大、最小値計算 表示している領域内で算出
   * @param data
   * @param dispSetting
   */
  const setYMinMaxEx = (datas: GraphData[], dispSetting: GraphDispSetting, isAutoRange: boolean): [number, number] => {
    var max = 0;
    var min = 0;
    let a: number = 0;
    let b: number = 0;
    let c: number = 0;
    if (datas[0]?.points.length !== 0) {
      var line1List = datas[0]?.points.map((r) => r.yValue);
      a = line1List == null ? 0 : Math.max.apply(null, line1List);
    }
    if (datas[1]?.points.length !== 0) {
      var line2List = datas[1]?.points.map((r) => r.yValue);
      b = line2List == null ? 0 : Math.max.apply(null, line2List);
    }
    if (datas[2]?.points.length !== 0) {
      var line3List = datas[2]?.points.map((r) => r.yValue);
      c = line3List == null ? 0 : Math.max.apply(null, line3List);
    }

    const array1 = [a, b, c];
    max = Math.max(...array1);

    if (dispSetting.selectAxis !== 'r') {
      max = dispSetting.yMax;
      min = dispSetting.yMin;
    } else {
      if (!isAutoRange) {
        //初期表示ではない場合、画面でセットされた値を使用
        max = dispSetting.yMax;
        min = dispSetting.yMin;
      }
    }
    //max = ToValueMaxRound(max);
    //min = ToValueMinRound(min);
    return [min, max];
  };

  /**
   * ツールチップ表示処理
   * @param param0 
   * @returns 
   */
  const CustomTooltip = ({ active, payload, label }: any) => {
    //console.debug(['payload', payload]); 
    if (active) {
      //閾値のカラーを取得
      let colorItem = thresholdgrapColor.current !== null ? thresholdgrapColor.current : 'black';
      console.debug('CustomTooltip1', [thresholdgraphLine, thresholdgraphLine.current, colorItem]);

      return (
        <div className={classes.customTooltip}>
          {/** 閾値表示 */}
          {thresholdgraphLine.current !== null &&
            <div className="label" style={{ color: colorItem !== undefined ? colorItem : 'black' }} >
              {`${thresholdgraphLine.current} : ${thresholdgraphYPoint.current}`}
            </div>
          }
          {payload?.map((r: any, index: any) => {
            if (index <= 2 && r.name !== 'yValue' && (r.name.includes('計測データ') || r.name.includes('Measure Data'))) {
              return (
                thresholdgraphLine.current === null &&
                <div key={r.name ?? index} style={{ color: tooltipColorSelect(r.color) }}>
                  {r.name} : {Number(r.value).toFixed(3)}
                </div>
              );
            }
          })}
          {/**周波数 */}
          <div className="label">{`${t('FFTComparisonChart.周波数', '周波数')} : ${Number(label).toFixed(3)}`}</div>
          {/** 各計測データの選択した周波数地点の値を表示 */}
          {payload?.map((r: any, index: any) => {
            if (index <= 2 && r.name !== 'yValue' && (!r.name.includes('計測データ') && !r.name.includes('Measure Data'))) {
              return (
                <div key={r.name ?? index} style={{ color: tooltipColorSelect(r.color) }}>
                  {r.name} : {Number(r.value).toFixed(3)}
                </div>
              );
            }
          })}
        </div>
      );
    } else {
      {/** 閾値表示 */ }
      console.debug('CustomTooltip2', [thresholdgraphLine]);
      return (
        <div className={classes.customTooltip}>
          {thresholdgraphLine.current !== null &&
            <div className="label">{`${thresholdgraphLine.current} : ${thresholdgraphYPoint.current}`}
            </div>
          }
        </div>
      )
    }
  };
  /**
   * ドットの閾値の文字色
   * @param color 
   * @returns 
   */
  const tooltipColorSelect = (color: any): any => {
    if (color == '#f8bbd0') {
      return '#f76397';
    }
    if (color == '#ffccbc') {
      return '#ff8080';
    }
    return color;
  }

  const chartControlMemo = useMemo(() => {
    console.debug(['FFTComparisonChart childMemo', graphData, thresholdgraphData, graphSetting]);
    return (
      <>
        <ResponsiveContainer className={props.chartContainerStyle} width="99%">
          <LineChart
            style={{ background: graphSetting.backColor }}
            width={500}
            height={300}
            margin={{ top: 10, right: 30, left: 20, bottom: 0 }}
            onMouseMove={(props) => {
              if (props.isTooltipActive) {
                if (props.activePayload?.length != 1) {
                  console.debug(['onMouseMove props', props]);
                }
              }
            }}
          >
            <CartesianGrid stroke={graphSetting.gridColor} strokeDasharray="3 3" />
            <XAxis
              tickCount={5}
              dataKey="xValue"
              type="number"
              allowDuplicatedCategory={false}
              stroke={graphSetting.memoryFontColor}
              domain={[graphSetting.xMin, graphSetting.xMax]}
              tickFormatter={(e) => Number(e).toFixed(0)}
              allowDataOverflow={true}
              height={50}
              tick={{ dy: 10 }}
              padding={{ right: 0, left: 1 }}
            >
              <Label angle={0} position="insideBottomRight" style={{ textAnchor: 'middle', padding: 0 }}>
                {graphSetting.xUnit}
              </Label>
            </XAxis>
            <YAxis
              yAxisId={1}
              width={95}
              //ticks={[0.000, 0.001, 0.15, 0.2, 0.25, 0.3, 0.35]}
              tickCount={10}
              dataKey="yValue"
              type="number"
              tickFormatter={(e) => Number(e).toFixed(3)}
              stroke={graphSetting.memoryFontColor}
              domain={[graphSetting.yMin, graphSetting.yMax]}
              //domain={['auto', 'auto']}
              allowDataOverflow={true}
            >
              <Label angle={270} position="insideLeft" style={{ textAnchor: 'middle', padding: 0 }}>
                {graphSetting.yUnit}
              </Label>
            </YAxis>
            {/**ツールチップ表示 */}
            <Tooltip content={<CustomTooltip />} />
            {/**閾値表示 */}
            {thresholdgraphData?.map((row, index) => (
              <Line
                yAxisId={1}
                dataKey="yValue"
                stroke={row.infoColor}
                data={row.points}
                strokeWidth={10}
                dot={false}
                name={row.lineName}
                hide={!row.selectFlg}
                activeDot={{ r: 8 }}
                isAnimationActive={false}
                onMouseEnter={() => {
                  console.debug('onMouseEnter');
                  handleMouseEnter(row.lineName, row.tooltipColor, row.points[0].yValue.toString());
                }}
                onMouseLeave={() => {
                  console.debug('onMouseLeave');
                  handleMouseLeave();
                }}
              />
            ))}
            {/**計測データグラフ表示 */}
            {graphData?.map((row, index) => (
              <Line
                yAxisId={1}
                connectNulls
                dataKey="yValue"
                stroke={row.infoColor}
                data={row.points}
                strokeWidth={2}
                dot={false}
                name={row.lineName}
                hide={!row.selectFlg}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </>
    );
  }, [graphData, thresholdgraphData, graphSetting]);

  return <>{chartControlMemo}</>;
};
export default FFTComparisonChart;
